import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Button, Popconfirm, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import apiClient from '../services/api';

const GiftCertificates = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const fetchData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/gift-certificates', {
        params: { page, per_page: pageSize },
      });
      const d = res.data;
      setData(d.data || []);
      setPagination((prev) => ({
        ...prev,
        current: d.current_page || page,
        total: d.total || 0,
      }));
    } catch {
      message.error('Failed to load gift certificates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/admin/gift-certificates/${id}`);
      message.success('Gift certificate deleted');
      fetchData(pagination.current);
    } catch {
      message.error('Failed to delete');
    }
  };

  const getStatus = (r) => {
    if (r.redeemed_at) return { text: 'Redeemed', color: 'blue' };
    if (r.expires_at && new Date(r.expires_at) < new Date())
      return { text: 'Expired', color: 'red' };
    if (!r.is_active) return { text: 'Inactive', color: 'default' };
    return { text: 'Active', color: 'green' };
  };

  const columns = [
    { title: 'Code', dataIndex: 'code', key: 'code' },
    {
      title: 'Original Amount',
      dataIndex: 'original_amount',
      key: 'original_amount',
      render: (v) => `$${v?.toFixed(2)}`,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (v) => `$${v?.toFixed(2)}`,
    },
    {
      title: 'Purchaser',
      key: 'purchaser',
      render: (_, r) => r.purchaser?.name || r.purchaser?.email || 'N/A',
    },
    { title: 'Recipient Email', dataIndex: 'recipient_email', key: 'recipient_email' },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => {
        const s = getStatus(r);
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Popconfirm title="Delete this gift certificate?" onConfirm={() => handleDelete(r.id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card title="Gift Certificates">
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (t) => `Total ${t} certificates`,
        }}
        onChange={(p) => fetchData(p.current, p.pageSize)}
      />
    </Card>
  );
};

export default GiftCertificates;
